import { Clock, House, Shield } from 'lucide-react';
import {
  getAppStoreUrl,
  getPlayStoreUrl,
  LANDING_STORES_COMING_SOON,
} from '@/constants/landing.constants';

const HERO_FEATURES = [
  { icon: House, label: 'Atendimento em casa' },
  { icon: Clock, label: 'Mais praticidade e agilidade' },
  { icon: Shield, label: 'Segurança e confidencialidade' },
] as const;

export function LandingHero() {
  const playStoreUrl = getPlayStoreUrl();
  const appStoreUrl = getAppStoreUrl();
  const hasStoreLinks = Boolean(playStoreUrl || appStoreUrl);

  return (
    <section id="inicio" className="scroll-mt-20 bg-white">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-2 lg:gap-12 lg:px-8 lg:py-20">
        <div>
          <p className="text-xs font-semibold tracking-[0.18em] text-teal-700 uppercase">
            Telemedicina e atendimento domiciliar
          </p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl lg:text-[3.25rem] lg:leading-[1.1]">
            Saúde que vai <span className="text-teal-700">até você</span>
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-neutral-600 sm:text-lg">
            Consultas por telemedicina, atendimento domiciliar e acompanhamento de saúde
            na palma da sua mão.
          </p>

          <ul className="mt-8 space-y-3">
            {HERO_FEATURES.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-sm text-neutral-800 sm:text-base">
                <span className="inline-flex size-9 items-center justify-center rounded-full bg-teal-50 text-teal-700">
                  <Icon className="size-5" aria-hidden />
                </span>
                {label}
              </li>
            ))}
          </ul>

          <div className="mt-8">
            {hasStoreLinks ? (
              <div className="flex flex-wrap gap-3">
                {playStoreUrl ? (
                  <StoreBadge href={playStoreUrl} store="play" />
                ) : null}
                {appStoreUrl ? (
                  <StoreBadge href={appStoreUrl} store="app" />
                ) : null}
              </div>
            ) : (
              <p className="text-sm font-medium text-neutral-700">{LANDING_STORES_COMING_SOON}</p>
            )}
          </div>
        </div>

        <div className="relative">
          <div className="overflow-hidden rounded-2xl bg-neutral-100 shadow-lg ring-1 ring-neutral-200/80">
            <img
              src="/images/capa.jpeg"
              alt="Consulta por telemedicina no aplicativo App Saúde"
              className="h-full w-full max-h-[28rem] object-cover lg:max-h-[32rem]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function StoreBadge({ href, store }: { href: string; store: 'play' | 'app' }) {
  const label = store === 'play' ? 'Disponível no Google Play' : 'Disponível na App Store';

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-3 rounded-xl bg-neutral-950 px-4 py-2.5 text-white transition-opacity hover:opacity-90"
    >
      {store === 'play' ? <GooglePlayGlyph /> : <AppleGlyph />}
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] text-neutral-300">Disponível no</span>
        <span className="text-sm font-semibold">{store === 'play' ? 'Google Play' : 'App Store'}</span>
      </span>
      <span className="sr-only">{label}</span>
    </a>
  );
}

function GooglePlayGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="size-6 shrink-0" aria-hidden>
      <path fill="#EA4335" d="M3.6 2.2 13.7 12 3.6 21.8A2.4 2.4 0 0 1 3 20.1V3.9c0-.7.2-1.3.6-1.7Z" />
      <path fill="#FBBC04" d="M3.6 21.8 13.7 12l4.3 4.2-12.4 7.1c-.7.4-1.5.3-2 .0Z" />
      <path fill="#4285F4" d="M20.4 10.2 18 8.8l-4.3 4.2 4.3 4.2 2.4-1.4c1.1-.6 1.1-2.3 0-3.6Z" />
      <path fill="#34A853" d="M3.6 2.2C4.1 1.9 4.9 1.8 5.6 2.2L18 9.3 13.7 12 3.6 2.2Z" />
    </svg>
  );
}

function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="size-6 shrink-0 fill-white" aria-hidden>
      <path d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.9-3.5.9-.7 0-1.9-.8-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.7 2.5 3 2.4 1.2-.1 1.6-.8 3.1-.8s1.8.8 3.1.7c1.3 0 2.1-1.2 2.9-2.4.9-1.3 1.3-2.6 1.3-2.6s-2.5-1-2.5-3.1ZM14.7 6.4c.7-.8 1.1-1.9 1-3-.9 0-2.1.6-2.8 1.4-.6.7-1.2 1.9-1 3 1 .1 2.1-.5 2.8-1.4Z" />
    </svg>
  );
}
